import { useRef, useState } from 'react'
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { type StyleProp, StyleSheet, View, type ViewProps, type ViewStyle } from 'react-native'
import { HelperText, Menu, Text, useTheme } from 'react-native-paper'
import { Button } from '@/ui/Button'

export type MenuOption<V = unknown> = {
    label: string
    value: V
    description?: string
    helperText?: string
    disabled?: boolean
}

export type MenuButtonProps<T extends FieldValues = Record<string, unknown>, V = unknown> = {
    name: Path<T>
    control: Control<T>
    label?: string
    options: MenuOption<V>[]
    hint?: string
    placeholder?: string
    disabled?: boolean
    buttonMode?: 'outlined' | 'contained' | 'text'
    buttonStyle?: StyleProp<ViewStyle>
    renderLabel?: (option: MenuOption<V>) => string
    wrapperProps?: ViewProps
}

export function MenuButton<T extends FieldValues = Record<string, unknown>, V = unknown>({
    name,
    control,
    label,
    options,
    hint,
    placeholder = 'Select an option',
    disabled = false,
    buttonMode = 'outlined',
    buttonStyle,
    renderLabel,
    wrapperProps = {},
}: MenuButtonProps<T, V>) {
    const theme = useTheme()
    const [visible, setVisible] = useState(false)
    const menuKeyRef = useRef(0)

    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })
    const selectedOption = options.find(opt => opt.value === field.value)

    const displayLabel = selectedOption
        ? (renderLabel?.(selectedOption) ?? selectedOption.label)
        : placeholder

    const handleSelect = (value: V) => {
        field.onChange(value)
        menuKeyRef.current += 1
        setVisible(false)
    }

    return (
        <View style={[styles.inputWrapper, wrapperProps.style]} {...wrapperProps}>
            {label && (
                <Text variant="bodyMedium" style={styles.menuLabel}>
                    {label}
                </Text>
            )}

            <Menu
                key={menuKeyRef.current}
                visible={visible}
                onDismiss={() => {
                    menuKeyRef.current += 1
                    setVisible(false)
                }}
                anchor={
                    <Button
                        title={displayLabel}
                        mode={buttonMode}
                        onPress={() => setVisible(true)}
                        disabled={disabled}
                        style={[styles.menuButton, buttonStyle]}
                    />
                }
            >
                {options.map((option, index) => (
                    <Menu.Item
                        key={String(option.value ?? index)}
                        onPress={() => {
                            if (option.disabled) return
                            handleSelect(option.value)
                        }}
                        disabled={option.disabled}
                        title={
                            option.description || option.helperText ? (
                                <View>
                                    <Text variant="bodyMedium">{option.label}</Text>
                                    {option.description && (
                                        <Text
                                            variant="bodySmall"
                                            style={[
                                                styles.menuOptionDescription,
                                                { color: theme.colors.onSurfaceVariant },
                                            ]}
                                        >
                                            {option.description}
                                        </Text>
                                    )}
                                    {option.helperText && (
                                        <Text
                                            variant="bodySmall"
                                            style={[
                                                styles.menuOptionHelper,
                                                { color: theme.colors.onSurfaceVariant },
                                            ]}
                                        >
                                            {option.helperText}
                                        </Text>
                                    )}
                                </View>
                            ) : (
                                option.label
                            )
                        }
                    />
                ))}
            </Menu>

            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 8,
    },
    menuLabel: {
        marginBottom: 8,
        fontWeight: '600',
    },
    menuButton: {
        minWidth: 150,
    },
    menuOptionDescription: {
        marginTop: 2,
    },
    menuOptionHelper: {
        fontStyle: 'italic',
        marginTop: 2,
        fontSize: 11,
    },
})
